import { useTranslation } from 'react-i18next';
import { FulfillmentData, FulfillmentFeeData, PackingFeeData } from '../../models/rate.types';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';

interface FulfillmentDataDisplayProps {
  data: FulfillmentData;
}

const FeeCard = ({
  title,
  fee,
  showAttachmentFee = false,
}: {
  title: string;
  fee: FulfillmentFeeData | PackingFeeData | undefined;
  showAttachmentFee?: boolean;
}) => {
  const { t } = useTranslation('ratesModule');
  const currency = useCurrency();

  if (!fee) return null;

  const attachmentFee = showAttachmentFee ? (fee as PackingFeeData).attachment_fee : undefined;

  return (
    <div className="border border-card-border rounded-lg overflow-hidden">
      <div className="bg-card-border/30 px-4 py-2 border-b border-card-border">
        <p className="font-medium text-title text-sm">{title}</p>
      </div>
      <div className="p-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-subtext">{t('itemData.fulfillment.fixedFee')}</span>
            <span className="text-sm font-medium text-title">{formatCurrencyAmount(fee.fixed_fee, currency)}</span>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-card-border">
            <div className="flex flex-col">
              <span className="text-xs text-subtext">{t('itemData.fulfillment.extraLinesFee')}</span>
              <span className="text-xs text-subtext mt-1">
                {t('itemData.fulfillment.sinceLineNumber')}: {fee.extra_lines_fee_since_line_number}
              </span>
            </div>
            <span className="text-sm font-medium text-title">
              {formatCurrencyAmount(fee.extra_lines_fee, currency)}
            </span>
          </div>
          {attachmentFee != null && (
            <div className="flex items-center justify-between pt-2 border-t border-card-border">
              <span className="text-xs text-subtext">{t('itemData.fulfillment.attachmentFee')}</span>
              <span className="text-sm font-medium text-title">{formatCurrencyAmount(attachmentFee, currency)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const FulfillmentDataDisplay = ({ data }: FulfillmentDataDisplayProps) => {
  const { t } = useTranslation('ratesModule');

  const hasData = data.packing_fee || data.reused_fee;

  if (!hasData) {
    return <p className="text-sm text-subtext italic">{t('itemData.noData')}</p>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {data.packing_fee && (
        <FeeCard title={t('itemData.fulfillment.packingFee')} fee={data.packing_fee} showAttachmentFee />
      )}
      {data.reused_fee && <FeeCard title={t('itemData.fulfillment.refusedFee')} fee={data.reused_fee} />}
    </div>
  );
};
